import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon, DownloadIcon, Loader2Icon, XIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { useAdminData } from '../../contexts/AdminDataContext';
import { downloadInvoicePdf, invoiceFileName, invoicePdfDataUrl } from '../../utils/invoicePdf';
import type { Invoice } from '../../types/invoice';
const ease = [0.23, 1, 0.32, 1] as const;
interface Props {
  invoice: Invoice;
  onClose: () => void;
}

/** Viser den færdige faktura-PDF — præcis samme fil som download. */
export function InvoicePdfPreview({
  invoice,
  onClose
}: Props) {
  const {
    settings
  } = useAdminData();
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    invoicePdfDataUrl(invoice, settings).then(next => {
      if (active) setUrl(next);
    }).catch(() => {
      if (active) setError('PDF’en kunne ikke dannes. Prøv igen.');
    });
    return () => {
      active = false;
    };
  }, [invoice, settings]);
  return <div className="fixed inset-0 z-50 flex flex-col bg-ink/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="faktura-pdf-titel" data-id="element-132" data-mp="2kndb0fle3kyt">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.24,
      ease
    }} className="mx-auto flex h-full w-full max-w-4xl flex-col overflow-hidden rounded-card bg-white" data-id="element-133" data-mp="2fq4sgdwbxex1">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4" data-id="element-134" data-mp="33eny0lnzvdlf">
          <div className="min-w-0" data-id="element-135" data-mp="2ylldf3173ehd">
            <h2 id="faktura-pdf-titel" className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-136" data-mp="2ykjpvz2zszir">
              Forhåndsvisning
            </h2>
            <p className="mt-0.5 truncate text-sm text-ink-muted" data-id="element-137" data-mp="3r48rvzlorv9h">
              {invoiceFileName(invoice.invoiceNumber)}
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-138" data-mp="1kex2bl5lusng">
            <Button variant="secondary" size="sm" onClick={() => void downloadInvoicePdf(invoice, settings)} data-id="element-139" data-mp="1kg85pjv51ye1">
              <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-140" data-mp="1apnam6eobbzv" />
              Download PDF
            </Button>
            <button type="button" onClick={onClose} aria-label="Luk forhåndsvisning" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink" data-id="element-141" data-mp="1fn6o343jrkqr">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-142" data-mp="rsccwq8owqmj" />
            </button>
          </div>
        </header>

        <div className="flex-1 bg-canvas" data-id="element-143" data-mp="wr734d8zlhgo">
          {error ? <p role="alert" className="flex items-center justify-center gap-2 p-10 text-sm text-danger" data-id="element-144" data-mp="2m4trl9fgqifk">
              <AlertCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-145" data-mp="2r2lwr554c11p" />
              {error}
            </p> : url ? <iframe title="Faktura som PDF" src={url} className="h-full w-full" data-id="element-146" data-mp="2w87ja2n08b75" /> : <p className="flex items-center justify-center gap-2 p-10 text-sm text-ink-muted" data-id="element-147" data-mp="39zm0q519k4c3">
              <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-148" data-mp="3a1gl9i8mhufn" />
              Danner PDF …
            </p>}
        </div>
      </motion.div>
    </div>;
}